/**
 * Pure budget maths (Phase 3, §6.2) — DB-free, unit-tested. The single site for
 * parsing de-DE budget input, deriving the progress band, and applying an
 * optimistic budget edit onto a cached `MonthViewOut` (so `useUpsertBudget` can
 * patch the `['reports','month',m]` cache and roll back on error).
 */
import { formatAmountInput, parseAmountInput } from '../../lib/format.ts';
import type {
  BucketGroupWithBuckets,
  Money,
  MonthBucketRow,
  MonthViewOut,
} from '../../lib/types.ts';

export interface ParsedBudget {
  /** True when the field is syntactically valid (empty counts as valid = clear). */
  valid: boolean;
  /** Canonical dot-decimal string, or `null` to clear. Meaningful only when `valid`. */
  value: Money | null;
}

/**
 * Parse an inline de-DE amount into a non-negative canonical `"150.00"` string
 * via the shared `parseAmountInput`. Empty clears the budget (`value = null`).
 * A leading `-`, letters, or > 2 decimal places are rejected (budgets are
 * positive spend limits — §2.1 / §3.5).
 */
export function parseBudgetInput(raw: string): ParsedBudget {
  if (raw.trim() === '') return { valid: true, value: null };
  const value = parseAmountInput(raw);
  if (value === null || value.startsWith('-')) return { valid: false, value: null };
  return { valid: true, value };
}

/** Render a canonical Money string as the inline input's de-DE editing value. */
export function formatBudgetInput(value: Money | null): string {
  return value === null ? '' : formatAmountInput(value);
}

/** True when the server included month data for this bucket (a budget or
 * activity) — the signal that a historical month still needs its row. */
function hasMonthData(row: MonthBucketRow | undefined): boolean {
  return row !== undefined && (row.budgeted !== null || row.txn_count > 0);
}

/**
 * The bucket tree the Budget month view should render (§6.2): active groups
 * keep all their active buckets, and archived buckets/groups stay visible for
 * months in which the server deliberately included them (budget set or
 * transactions booked — reports_query keeps them in the totals), so the
 * rendered rows always sum to the month totals.
 */
export function visibleBudgetGroups(
  groups: BucketGroupWithBuckets[],
  rowsByBucket: ReadonlyMap<number, MonthBucketRow>,
): BucketGroupWithBuckets[] {
  return groups
    .map((group) => ({
      ...group,
      buckets: group.buckets.filter((bucket) =>
        group.archived || bucket.archived ? hasMonthData(rowsByBucket.get(bucket.id)) : true,
      ),
    }))
    .filter((group) => !group.archived || group.buckets.length > 0);
}

export type ProgressBand = 'ok' | 'warn' | 'over';

export interface BudgetProgress {
  /** Percent of budget spent (0..∞); 0 when there is no positive budget. */
  pct: number;
  /** Bar-fill width in percent, capped at 100. */
  barPct: number;
  /** emerald (≤80) / amber (80–100) / rose (>100). */
  band: ProgressBand;
  /** `budget − spent` (negative = over budget); `null` when no budget. */
  remaining: number | null;
  over: boolean;
}

/** Derive the progress band + bar width for one bucket row (§6.2). */
export function budgetProgress(spent: number, budget: number | null): BudgetProgress {
  const hasBudget = budget !== null && budget > 0;
  const pct = hasBudget ? (spent / (budget as number)) * 100 : 0;
  const barPct = Math.max(0, Math.min(pct, 100));
  const band: ProgressBand = pct > 100 ? 'over' : pct > 80 ? 'warn' : 'ok';
  const remaining = budget !== null ? budget - spent : null;
  return { pct, barPct, band, remaining, over: pct > 100 };
}

const toNum = (m: Money): number => {
  const n = Number.parseFloat(m);
  return Number.isFinite(n) ? n : 0;
};

/** Format a number back to a canonical dot-decimal Money string (2dp). */
export function toMoney(n: number): Money {
  // Guard the JS `-0` case so a cleared row never renders `"-0.00"`.
  const safe = Object.is(n, -0) ? 0 : n;
  return safe.toFixed(2);
}

/**
 * Apply an optimistic budget edit onto a cached month view: patch the target
 * row's `budgeted`/`remaining`/`progress` and recompute the budgeted + remaining
 * grand totals over the non-excluded rows. `spent` is unchanged by a budget edit.
 */
export function patchMonthViewBudget(
  view: MonthViewOut,
  bucketId: number,
  amount: Money | null,
): MonthViewOut {
  const buckets = view.buckets.map((row) => {
    if (row.bucket_id !== bucketId) return row;
    const spent = toNum(row.spent);
    const budgetNum = amount !== null ? toNum(amount) : null;
    const remaining = budgetNum !== null ? budgetNum - spent : null;
    const progress = budgetNum !== null && budgetNum > 0 ? spent / budgetNum : null;
    return {
      ...row,
      budgeted: amount,
      remaining: remaining !== null ? toMoney(remaining) : null,
      progress,
    };
  });

  let budgetedTotal = 0;
  for (const row of buckets) {
    if (row.excluded_from_reports) continue;
    if (row.budgeted !== null) budgetedTotal += toNum(row.budgeted);
  }
  const spentTotal = toNum(view.totals.spent_total);

  return {
    ...view,
    buckets,
    totals: {
      ...view.totals,
      budgeted_total: toMoney(budgetedTotal),
      remaining_total: toMoney(budgetedTotal - spentTotal),
    },
  };
}
